import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { ChevronRight } from 'lucide-react';
import {
  PIKKO_JACKPOT_DISPLAY_TYPE,
  PIKKO_JACKPOT_TARGET,
} from '../../constants/pikkoJackpotConstants';
import { animateJackpotCounter } from '../../utils/helper-functions';
import { Header } from './PikkoJackpotConvertionHeader';
import { PikkoJackpotFloatingCoins } from './PikkoJackpotFloatingCoins';
import { PikkoJackpotPriceCounter } from './PikkoJackpotPriceCounter';
import { PikkoJackpotUrgencyCard } from './card/PikkoJackpotUrgencyCard';
import { PikkoJackpotBarcodeSection } from './PikkoJackpotBarcodeSection';
import { PikkoJackpotReactivateButton } from './button/PikkoJackpotReactivateButton';
import { PikkoJackpotDownloadButton } from './button/PikkoJackpotDownloadButton';

export const PikkoJackpotConvertionContent = ({
  isOpen,
  displayType,
  setIsOpen,
  daysRemaining,
  expiresAt,
  cartItems,
  onReactivate,
  reactivateLoading,
}) => {
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const [counterDone, setCounterDone] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [showBarcode, setShowBarcode] = useState(false);
  const timersRef = useRef([]);

  const isReminder = displayType === PIKKO_JACKPOT_DISPLAY_TYPE.REMINDER;
  const isLastChance = displayType === PIKKO_JACKPOT_DISPLAY_TYPE.LAST_CHANCE;
  const displayDays = Math.min(daysRemaining, 7);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const steps = Math.max(Math.floor(PIKKO_JACKPOT_TARGET), 1);
    const counterDuration = steps * 240;

    animateJackpotCounter(PIKKO_JACKPOT_TARGET, setCount, timersRef);

    const doneTimer = setTimeout(() => setCounterDone(true), counterDuration + 100);

    return () => {
      clearTimeout(doneTimer);
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [visible, PIKKO_JACKPOT_TARGET]);

  useEffect(() => {
    if (!counterDone) return;
    const t = setTimeout(() => setCtaVisible(true), 600);
    return () => clearTimeout(t);
  }, [counterDone]);

  useEffect(() => {
    if (!isOpen) {
      window.dispatchEvent(new Event('pikkoJackpotModalClosed'));
    }
  }, [isOpen]);

  return (
    <div
      className={clsx(
        'fixed inset-0 z-[1510] bg-white flex flex-col justify-between font-[DM_Sans,sans-serif] transition-opacity duration-300 ease-in-out overflow-y-auto h-[100dvh]',
        { 'opacity-0 pointer-events-none': !isOpen },
      )}
      id="jackpot-modal"
    >
      {/* ─── Top section — tinted bg + wave ─── */}
      <div className="relative flex flex-col items-center pt-16 px-8 pb-8 shrink-0 h-[46%]">
        {/* Background layers — gold always underneath, gray fades out */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #FFFCE8 0%, #FFFAF0 50%, #FFF4E5 100%)' }} />
        <div
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{
            background: 'linear-gradient(180deg, #F3F4F6 0%, #E5E7EB 100%)',
            opacity: isLastChance ? 1 : 0,
          }}
        />

        <div className="relative z-[1] flex items-center w-full px-2" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="flex-1 flex justify-center">
            <Header badgeVisible={visible} cartItems={cartItems} />
          </div>
          <button
            type="button"
            className='z-10 w-12 h-12 flex items-center justify-center rounded-full bg-black/5 active:bg-black/10 transition-colors duration-150 shrink-0'
            onClick={() => setIsOpen(false)}
          >
            <ChevronRight size={20} className='text-gray-600' strokeWidth={2.5} />
          </button>
        </div>

        {/* Big price — counting up + floating coins */}
        <div className='relative z-[1] flex-1 flex items-center justify-center w-full'>
          <PikkoJackpotPriceCounter visible={visible} isLastChance={isLastChance} count={count} />
          <PikkoJackpotFloatingCoins visible={visible} isLastChance={isLastChance} />
        </div>

        {/* Wave divider */}
        <svg
          aria-hidden="true"
          className="absolute -bottom-[1px] left-0 w-full pointer-events-none drop-shadow-[0_-4px_6px_rgba(0,0,0,0.06)] z-[1]"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ height: 48 }}
        >
          <path
            d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z"
            fill="white"
          />
        </svg>
      </div>

      {/* ─── Middle section — scrollable ─── */}
      <div className="flex flex-col flex-1 overflow-y-auto [@media(max-height:850px)]:overflow-y-hidden w-full items-center px-8 mt-10 [@media(max-height:700px)]:mt-6 pb-4 [@media(max-height:700px)]:pb-2">
        <div className='flex flex-col gap-16 [@media(max-height:850px)]:gap-6 items-center'>

          <div className='flex flex-col items-center gap-4 justify-center'>
            <h2
              className={clsx(
                'text-[20px] [@media(max-height:800px)]:text-[17px] [@media(max-height:700px)]:text-[15px] font-bold text-gray-900 text-center leading-tight font-[Fredoka,sans-serif] transition-all duration-500 delay-100',
                counterDone
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-3',
              )}
            >
              {isLastChance
                ? `Tes ${Math.round(PIKKO_JACKPOT_TARGET)}€ ont expiré`
                : `${Math.round(PIKKO_JACKPOT_TARGET)}€ t'attendent`
              }
            </h2>

            <p
              className={clsx(
                'text-[15px] [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:text-[11px] text-gray-500 text-center leading-relaxed max-w-[280px] transition-all duration-500 delay-200',
                counterDone
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-3',
              )}
            >
              {isLastChance
                ? 'Réactive ta cagnotte maintenant avant qu\'elle ne disparaisse'
                : 'Télécharge l\'application avec le bouton ci-dessous et fais augmenter ta cagnotte'}
            </p>
          </div>

          <PikkoJackpotUrgencyCard isLastChance={isLastChance} displayDays={displayDays} expiresAt={expiresAt} counterDone={counterDone} />

        </div>
      </div>

      {/* ─── Bottom CTA — fixed ─── */}
      <div
        className={clsx(
          'shrink-0 bg-white px-8 transition-all duration-500 gap-6 [@media(max-height:700px)]:gap-4 flex flex-col w-full [@media(max-height:700px)]:!pb-3',
          ctaVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4',
        )}
        style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom))', paddingTop: '16px' }}
      >
        <div className="pb-2">
          {isLastChance ? (
            <PikkoJackpotReactivateButton onReactivate={onReactivate} reactivateLoading={reactivateLoading} setIsOpen={setIsOpen} />
          ) : (
            <PikkoJackpotDownloadButton isReminder={isReminder} target={PIKKO_JACKPOT_TARGET} />
          )}
        </div>

        {cartItems?.checkoutBarcode && (
          <PikkoJackpotBarcodeSection
            checkoutBarcode={cartItems.checkoutBarcode}
            showBarcode={showBarcode}
            setShowBarcode={setShowBarcode}
          />
        )}

        <button
          type="button"
          className='text-gray-800 text-[15px] [@media(max-height:700px)]:text-[13px] font-medium underline underline-offset-4 decoration-gray-300 cursor-pointer text-center active:text-gray-600 transition-colors duration-150 pb-2 [@media(max-height:700px)]:pb-1 bg-transparent border-none w-full'
          onClick={() => setIsOpen(false)}
        >
          Passer et afficher le reçu
        </button>
      </div>
    </div>
  );
};
